import { Component, EventEmitter, OnInit, Output, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { CustomValidators } from 'src/validators';
import * as STATES from 'src/assets/states.json';
import { ViaCepService } from 'src/services/via-cep.service';
import { debounceTime, map } from 'rxjs/operators';
import { PlatformLocation } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { HttpService } from 'src/services/http.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import * as accents from 'remove-accents';
import * as moment from 'moment';
import { ErrorReportService } from 'src/services/error-report.service';

@Component({
  selector: 'app-step-two',
  templateUrl: './step-two.component.html',
  styleUrls: ['./step-two.component.scss']
})
export class StepTwoComponent implements OnInit {

  form: FormGroup;
  @Output() formObserver: Subject<FormGroup>;
  @Output() submit: EventEmitter<any>;
  @Input() initData: EventEmitter<any>;
  states: Array<any> = (STATES as any).default;
  mauticFormSrc: any = null;
  genders$: Observable<any>;
  cities: Array<any> = [];
  filteredStates$: Observable<any>;
  filteredCities$: Observable<any>;

  constructor(
    fb: FormBuilder,
    private viaCep: ViaCepService,
    private sanitazer: DomSanitizer,
    private pLocation: PlatformLocation,
    private http: HttpService,
    private errorReport: ErrorReportService,
    //private http: HttpService
  ) {

    this.form = fb.group({
      email: [localStorage.getItem('email'), Validators.required],
      gender: [null, Validators.required],
      birthdate: [null, Validators.required],
      zipCode: [null, [Validators.required, CustomValidators.cep]],
      state: [null, Validators.required],
      stateName: [null, Validators.required],
      city: [null, Validators.required],
      district: [null, Validators.required],
      street: [null, Validators.required],
      number: [null, Validators.required],
      complement: [null],
      acceptTerm: [true, Validators.required]
    });
    this.submit = new EventEmitter<any>();
    this.formObserver = new Subject<FormGroup>();

  }

  ngOnInit() {

    this.formObserver.next(this.form);
    this.form.get('zipCode')
      .valueChanges
      .pipe(debounceTime(500))
      .subscribe(
        cep => {
          if (!this.form.get('zipCode').invalid) {
            this.viaCep.getLocation(cep)
              .subscribe(
                data => {

                  const { bairro, localidade, logradouro, uf } = data;
                  const state = this.states.find(state => state
                    ? state.sigla.toLowerCase() ===
                    uf.toLowerCase() : null);

                  this.form.patchValue({
                    district: bairro,
                    city: localidade,
                    street: logradouro,
                    state: uf,
                    // state: state ? state.sigla : null,
                    stateName: state ? accents.remove(state.nome) : null,
                    email: localStorage.getItem('email')
                  }, { emitEvent: false });

                }
              )
          }
        }
      )

    this.genders$ = this.http.get('gender');

    this.filteredStates$ = this.form.get('state')
      .valueChanges
      .pipe(
        map(state => this._filterStates(state))
      );

    this.filteredCities$ = this.form.get('city')
      .valueChanges
      .pipe(map(city => this._filterCities(city)));
      
  }

  ngAfterViewInit() {

    if (this.initData) {
      this.initData.subscribe((data) => {

        let birthdate = null;

        if (data['user'].birthdate) {

          const parts = data['user'].birthdate.split('-');
          birthdate = parts[2] + '/' + parts[1] + '/' + parts[0];

        }

        this.form.patchValue(
          {
            email: data['user'].email,
            gender: data['user'].gender,
            birthdate,
            zipCode: data['user'].zipcode,
            // state: data['user'].state,
            city: data['user'].city,
            district: data['user'].district,
            street: data['user'].address1,
            number: data['user'].address2,
            complement: data['user'].complement
          },
          { emitEvent: false }
        );

        if (data['user'].zipcode) {

          this.viaCep.getLocation(data['user'].zipcode)
            .subscribe(
              data => {

                const { bairro, localidade, logradouro, uf } = data;
                const state = this.states.find(state => state
                  ? state.sigla.toLowerCase() ===
                  uf.toLowerCase() : null);

                this.form.patchValue({
                  district: bairro,
                  city: localidade,
                  street: logradouro,
                  state: uf,
                  stateName: state ? accents.remove(state.nome) : null,
                }, { emitEvent: false });

              }
            )

        }

      });
    }

  }

  onSubmit(): void {

    if (!this.form.invalid) {

      const data = this.form.getRawValue();
      const params = new URLSearchParams();
      data.birthdate = moment(data.birthdate, 'DD/MM/YYYY').format('YYYY-MM-DD');

      for (let key in data) {

        const value = data[key];
        if (value) {
          if (key === 'state' && typeof value === 'object')
            params.append(key, value.sigla);
          else
            params.append(key, value);
        }

      }

      this.trueAprove();

      const bHref = this.pLocation.getBaseHrefFromDOM();
      this.mauticFormSrc = this.sanitazer
        .bypassSecurityTrustResourceUrl(`${bHref}mautic-form/step-two?${params.toString()}`);
      this.submit.emit();

    }

  }

  public displayState(state: any): string {
    if (state && typeof state === 'string')
      return state;

    return state ? state.sigla : null
  }

  public onStateSelected(args: MatAutocompleteSelectedEvent) {

    const { option: { value } } = args;

    this.form.get('stateName').setValue(accents.remove(value.nome));
    this.http.list('locality:city', { initials: value.sigla })
      .subscribe(
        (cities: any) => {
          this.cities = cities;
        }
      );

  }

  private _filterCities(city) {
    city = city.toLowerCase();
    return this.cities.filter(_city => _city.name.toLowerCase().indexOf(city) > -1);
  }

  private _filterStates(state) {

    if (typeof state !== 'string') return;

    let name = accents.remove(state.toLowerCase());
    return this.states.filter(_state => accents.remove(_state.nome).toLowerCase().indexOf(name) > -1
      || _state.sigla.toLowerCase().indexOf(name) > -1);

  }

  public trueAprove(){

    const data = {
      email: localStorage.getItem('email')
    };

    this.http.save('profile:approve', data)
        .subscribe(
          (result: any) => {
            if (result.type === 'OK')
              console.warn('candidato update');
          },
          err => this.errorReport.showMessage(err)
        );

  }

}